import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { createSelector } from 'reselect'

import { useTheme } from '@material-ui/styles'
import {
  Button,
  Divider,
  Typography,
  Grid,
  makeStyles,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton
} from '@material-ui/core'

import {
  ArrowBack,
  ZoomOutMap,
  Directions,
  ChevronRight,
  Phone,
  WhatsApp,
  PhotoLibrary
} from '@material-ui/icons'

import useViewportSizes from '~/components/useViewportSize'
import WhatsappFormDialog from '~/containers/WhatsAppForm'
import { AppRouterContext } from '~/Router'
import { useLiterals } from '~/components/Literals'

import track, { cliqueRota, cliqueWhats, log } from '~/track'
import { formatPhoneNumber } from '~/utils'
import { selectStores } from '~/selectors'

import { StoreDialogImages } from './StoreDialogImages'
import { convertPeriod } from './utils/convert-period'
import ButtonStoreRoute from './ButtonStoreRoute'
import ListItemPeriod from './ListItemPeriod'
import ProductItem from './ProductItem'

import Header from '~/components/Header'
import CustomDrawer from '~/components/Drawer'
import { getWhatsAppUrl } from '~/utils'
import actions from '~/actions'
import api from '~/api'

import GmbAttributesPreview from './GmbAttributesPreview'
import GmbAttributesDetailsDialog from './GmbAttributesDetailsDialog'

const selector = createSelector(
  (state) => state.config,
  (state) => state.app.currentProduct,
  (config, currentProduct) => ({ config, currentProduct })
)

const useStyles = makeStyles((theme) => ({
  list: { width: '100%' },
  listNav: {
    padding: '22px 0'
  },
  listItem: {
    paddingBlock: 16
  },
  iconWhatsapp: {
    width: 30,
    height: 30,
    padding: 4,
    borderRadius: 9999,
    backgroundColor: '#34AF23',
    color: '#fff'
  },
  iconPhone: {
    width: 30,
    height: 30,
    padding: 4,
    borderRadius: 9999,
    color: '#97A8AB'
  },
  back: {
    gap: 8,
    display: 'flex',
    alignItems: 'center',
    marginTop: 4
  },
  listItemText: {
    color: '#000',
    [theme.breakpoints.up('md')]: {
      textDecoration: 'underline'
    },
    '& .MuiTypography-body2': {
      fontSize: 16
    }
  }
}))

function StoreInformations({ history, position }) {
  const { dispatch: appDispatcher } = useContext(AppRouterContext)

  const theme = useTheme()
  const viewport = useViewportSizes()
  const classes = useStyles()
  const dispatch = useDispatch()
  const isMobile = viewport.width < theme.gofind.desktopMaxWidth

  const { currentStore, showStoreInformation } = useSelector(selectStores)
  const { config, currentProduct } = useSelector(selector)
  const { getLiteral } = useLiterals()

  const [didMount, setDidMount] = useState(false)
  const [products, setProducts] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [openForm, setOpenForm] = useState(false)
  const [isOpenImages, setIsOpenImages] = useState(false)
  const [showPhone, setShowPhone] = useState(false)
  const [openGmbAttribute, setOpenGmbAttribute] = useState(false)
  const [gmbAttributes, setGmbAttributes] = useState([])

  const [isLoadingGmb, setIsLoadingGmb] = useState(false)
  const [period, setPeriod] = useState(null)
  const [images, setImages] = useState([])

  const phoneNumberFormatted = formatPhoneNumber(currentStore?.phoneNumber)
  const [startPhoneNumber] = phoneNumberFormatted?.split('-')

  const linkWhatAppUrl = currentStore?.whatsapp
    ? getWhatsAppUrl(
        currentStore?.whatsapp,
        currentStore?.name,
        currentProduct?.name ||
          Object.values(currentStore.stats)
            .map((i) => i.name)
            .join(),
        config?.brandName,
        !!config?.showContextViewEstablishment,
        getLiteral
      )
    : ''

  useEffect(() => {
    setDidMount(true)

    return () => setDidMount(false)
  }, [])

  useEffect(() => {
    async function handleGmbStore() {
      setPeriod(null)
      setImages([])

      if (currentStore?.cnpj && currentStore?.gmbStatus === 'INTEGRATED') {
        try {
          setIsLoadingGmb(true)

          const response = await api.gmbStore(currentStore.cnpj)
          const {
            data: { regularHours },
            media
          } = response

          Array.isArray(response?.gmbAttributes?.attributes) &&
            setGmbAttributes(response?.gmbAttributes?.attributes)
          setPeriod(convertPeriod(regularHours.periods))
          media?.mediaItems && setImages(media?.mediaItems)
        } catch (err) {
          setPeriod(null)
          setImages([])
          console.log('TCL: ~ handleGmbStore ~ err', err)
        } finally {
          setIsLoadingGmb(false)
        }
      } else {
        setPeriod(null)
      }
    }

    handleGmbStore()
  }, [currentStore?.cnpj])

  useEffect(() => {
    setProducts([])

    async function handleStoreProducts() {
      if (currentStore?.cnpj) {
        try {
          setIsLoading(true)

          const response = await api.getStoreProducts(currentStore.cnpj)
          setProducts(response.products)
        } catch (err) {
          console.log('TCL: ~ handleStoreProducts ~ err', err)
        } finally {
          setIsLoading(false)
        }
      }
    }

    handleStoreProducts()
  }, [currentStore?.cnpj])

  useEffect(() => {
    if (didMount && currentProduct?.gtin) history.push(`/produto/${currentProduct?.gtin}`)
  }, [position.user?.lat, position.user?.lng, currentProduct])

  const handleGoRoute = useCallback(() => {
    cliqueRota(config, currentStore, currentProduct, position?.user)
  }, [currentStore, currentProduct, config, position])

  const handleSeePhone = (e) => {
    if (isMobile) {
      track('cliqueVerTelefone', { category: 'menu', name: currentStore?.storeName })
      track('cliqueLigar', { name: currentStore?.name, category: 'loja' })
      log({ event: 'call' }, config, currentStore, currentProduct, position?.user)
      window.open(`tel:${phoneNumberFormatted}`)
      return
    }

    if (!showPhone) {
      e.stopPropagation()
      e.preventDefault()
      setShowPhone(true)

      track('cliqueVerTelefone', { category: 'menu', name: currentStore?.name })
      log({ event: 'call' }, config, currentStore, currentProduct, position?.user)
      return
    }

    track('cliqueLigar', { name: currentStore?.name, category: 'loja' })
    window.open(`tel:${phoneNumberFormatted}`)
  }

  const handleProductClick = (product) => {
    log({ event: 'product' }, config, currentStore, product, position?.user)

    history.push({
      pathname: `/produto/${product.gtin}/detalhes`,
      state: { fromApp: true, prevPath: history.location.pathname }
    })
  }

  const handleOpenImages = () => {
    setIsOpenImages(true)
    track('cliqueFotosLoja', { category: 'loja', name: currentStore?.name })
  }

  const handleGoBack = () => {
    dispatch(actions.showStoreInformation(false))

    config.showContextViewStore || config.showContextViewEstablishment
      ? history.replace('/produto/todos')
      : history.replace('/')
  }

  const mapRouteCommunProps = useMemo(
    () => ({
      'aria-label': 'Route',
      onClick: handleGoRoute,
      'data-cy': 'route',
      data_analytics_category: 'onde-comprar',
      data_analytics_action: 'get_directions',
      data_analytics_partner_name: currentStore?.name,
      data_analytics_partner_address: currentStore?.coordinates?.formattedAddress,
      data_analytics_product_id: currentProduct?.gtin,
      data_analytics_product_name: currentProduct?.name,
      data_analytics_product_size: currentProduct?.name,
      data_analytics_product_type: currentProduct?.name,
      data_analytics_local: position?.user?.city
    }),
    [currentStore, currentProduct, position]
  )

  useEffect(() => {
    if (!currentStore)
      !!config.showContextViewStore || !!config.showContextViewEstablishment
        ? history.push('/produto/todos')
        : history.push('/')
  }, [])

  const handleOpenDialogGmbAttibute = () => {
    track('cliqueGMBatributos')
    setOpenGmbAttribute(true)
  }

  return (
    <>
      <StoreDialogImages
        store={currentStore}
        images={images}
        isOpen={isOpenImages}
        setIsOpen={setIsOpenImages}
      />

      {/* dialog detalhes dos atributos do gmb */}
      <GmbAttributesDetailsDialog
        open={openGmbAttribute}
        setOpen={setOpenGmbAttribute}
        attributes={gmbAttributes}
        isMobile={isMobile}
      />

      <CustomDrawer id="drawer-store-detail" open={showStoreInformation}>
        {!isMobile && <Header config={config} position={position} />}
        <Box display="flex" justifyContent="space-between" style={{ padding: '16px 16px 0' }}>
          {!!config.showContextViewStore || !!config.showContextViewEstablishment ? (
            <Grid className={classes.back}>
              <IconButton aria-label={getLiteral('1051')} onClick={handleGoBack} size="small">
                <ArrowBack style={{ color: theme.palette.text.primary }} />
              </IconButton>
              {!isMobile ? <Typography variant="body1">{getLiteral('1051')}</Typography> : null}
            </Grid>
          ) : null}

          {isMobile && (
            <Button
              variant="text"
              startIcon={<ZoomOutMap />}
              onClick={() => {
                appDispatcher({ current: 0, offset: 0, isListVisible: false })
                handleGoBack()
              }}
              style={{ textTransform: 'none' }}
            >
              {getLiteral('2545')}
            </Button>
          )}
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          flexWrap="nowrap"
          style={{ padding: 16 }}
        >
          <Box style={{ maxWidth: 'calc(100% - 50px - 16px)' }}>
            <Typography
              variant="h1"
              style={{
                fontSize: 24,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontWeight: theme.typography.fontWeightBold
              }}
            >
              {currentStore?.name || ''}
            </Typography>
            <Typography variant="body1">{currentStore?.coordinates?.formattedAddress}</Typography>
          </Box>
          <Box style={{ alignSelf: 'center' }}>
            <ButtonStoreRoute mapRouteCommunProps={mapRouteCommunProps} isMobile={isMobile} />
          </Box>
        </Box>

        {/* Exibição da previa dos attrributes mobile */}
        {currentStore?.gmbStatus === 'INTEGRATED' && gmbAttributes.length > 0 ? (
          <Box style={{ padding: '0 16px' }}>
            <GmbAttributesPreview
              handleOpen={handleOpenDialogGmbAttibute}
              isMobile={isMobile}
              gmbAttributes={gmbAttributes}
            />
          </Box>
        ) : null}

        <List className={`${classes.listNav} ${classes.list}`} component="nav">
          {/* Ver rota */}
          <Divider key="router-divider" />
          <ListItem button {...mapRouteCommunProps} key="router" className={classes.listItem}>
            <ListItemIcon>
              <Directions style={{ color: '#97A8AB' }} />
            </ListItemIcon>
            <ListItemText className={classes.listItemText} primary={getLiteral('2810')} />
            {isMobile ? <ChevronRight style={{ color: '#97A8AB' }} /> : null}
          </ListItem>

          {/* ligar */}
          {!!currentStore?.phoneNumber?.trim() && (
            <>
              <Divider key="phone-divider" />
              <ListItem
                button
                component="a"
                data-cy="call"
                onClick={handleSeePhone}
                className={classes.listItem}
                target="_blank"
                key="phone"
              >
                <ListItemIcon>
                  <Phone className={classes.iconPhone} />
                </ListItemIcon>
                <ListItemText
                  className={classes.listItemText}
                  primary={
                    isMobile
                      ? getLiteral('1514')
                      : showPhone
                      ? phoneNumberFormatted
                      : `${startPhoneNumber}-... ${getLiteral('1315').toLocaleLowerCase()}`
                  }
                />
                {isMobile ? <ChevronRight style={{ color: '#97A8AB' }} /> : null}
              </ListItem>
            </>
          )}

          {/* whatsapp */}
          {currentStore?.whatsapp && (
            <>
              <Divider key="whatsapp-divider" />
              {!!config?.whatsappForm ? (
                <ListItem
                  button
                  onClick={() => {
                    setOpenForm(true)
                  }}
                  className={classes.listItem}
                  key="whatsapp"
                >
                  <ListItemIcon>
                    <WhatsApp className={classes.iconWhatsapp} style={{ color: '#fff' }} />
                  </ListItemIcon>
                  <ListItemText className={classes.listItemText} primary={getLiteral('2473')} />
                  {isMobile ? <ChevronRight style={{ color: '#97A8AB' }} /> : null}
                </ListItem>
              ) : (
                <ListItem
                  button
                  component="a"
                  href={linkWhatAppUrl}
                  className={classes.listItem}
                  target="_blank"
                  onClick={() => cliqueWhats(currentProduct)}
                  key="whatsapp"
                >
                  <ListItemIcon>
                    <WhatsApp className={classes.iconWhatsapp} style={{ color: '#fff' }} />
                  </ListItemIcon>
                  <ListItemText className={classes.listItemText} primary={getLiteral('2473')} />
                  {isMobile ? <ChevronRight style={{ color: '#97A8AB' }} /> : null}
                </ListItem>
              )}
            </>
          )}

          {/* funcionamento */}
          {!isLoadingGmb && period && (
            <ListItemPeriod
              period={period}
              isLoadingGmb={isLoadingGmb}
              store={currentStore}
              key="period"
            />
          )}

          {/* fotos */}
          {images.length > 0 ? (
            <>
              <Divider key="image-divider" />
              <ListItem button onClick={handleOpenImages} key="image" className={classes.listItem}>
                <ListItemIcon>
                  <PhotoLibrary style={{ color: '#97A8AB' }} />
                </ListItemIcon>
                <ListItemText className={classes.listItemText} primary={getLiteral('2560')} />
                {isMobile ? <ChevronRight style={{ color: '#97A8AB' }} /> : null}
              </ListItem>
              <Divider key="last-divider" />
            </>
          ) : null}
        </List>

        <Typography variant="subtitle1" style={{ marginLeft: 16 }}>{`${getLiteral(
          '2599'
        )}:`}</Typography>

        <List className={classes.list}>
          {!!products && !isLoading
            ? Array.isArray(products) &&
              products.map((product) => (
                <ListItem
                  button
                  onClick={() => handleProductClick(product)}
                  style={{ paddingTop: 16, paddingBottom: 16 }}
                  key={product?.gtin}
                >
                  <ProductItem product={product} />
                </ListItem>
              ))
            : [1, 2, 3].map((num) => (
                <ListItem style={{ paddingTop: 16, paddingBottom: 16 }} key={num}>
                  <ProductItem isSkeleton />
                </ListItem>
              ))}
        </List>
      </CustomDrawer>

      <WhatsappFormDialog
        open={openForm}
        setOpen={setOpenForm}
        track={() => cliqueWhats(currentProduct)}
      />
    </>
  )
}

export default StoreInformations
